import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CartData, CartItem } from "../types/cart.types";
import { cartChange, getCartItemForProduct } from "../utils/cart";

type CartItemKey = {
    id: number;
    category: string;
    dateKey: string;
    dateISO: string;
    time?: string;
};

type UseCartAmountSyncOptions = {
    cartData: CartData | null;
    cartLoaded?: boolean;
    reloadCart?: () => void;
    itemKey: CartItemKey;
};

type UseCartAmountSyncResult = {
    cartItem?: CartItem;
    cartAmount: number;
    setCartAmount: React.Dispatch<React.SetStateAction<number>>;
    handleCart: (amount: number) => void;
};

const useCartAmountSync = ({
    cartData,
    cartLoaded,
    reloadCart,
    itemKey,
}: UseCartAmountSyncOptions): UseCartAmountSyncResult => {
    const cartItem = useMemo(
        () => getCartItemForProduct(cartData?.items, itemKey),
        [cartData?.items, itemKey.category, itemKey.dateKey, itemKey.id, itemKey.time]
    );

    const [cartAmount, setCartAmount] = useState<number>(() => cartItem?.amount ?? 0);
    const isAmountChangingRef = useRef(false);

    useEffect(() => {
        if (cartLoaded === false) return;
        const next = cartItem?.amount ?? 0;
        if (!isAmountChangingRef.current && next !== cartAmount) {
            setCartAmount(next);
        }
    }, [cartLoaded, cartAmount, cartItem?.amount]);

    const debouncedReload = useMemo(() => {
        if (!reloadCart) return undefined;
        let refreshTimeout: number | null = null;
        return () => {
            if (refreshTimeout) window.clearTimeout(refreshTimeout);
            refreshTimeout = window.setTimeout(() => {
                reloadCart();
                refreshTimeout = null;
            }, 250);
        };
    }, [reloadCart]);

    const handleCart = useCallback(
        (amount: number) => {
            setCartAmount(amount);
            isAmountChangingRef.current = true;

            const timeValue = itemKey.time ?? cartItem?.time;

            Promise.resolve(
                cartChange(
                    cartData,
                    itemKey.id,
                    itemKey.dateISO,
                    timeValue,
                    amount,
                    itemKey.category,
                    itemKey.dateISO,
                    timeValue,
                    debouncedReload
                )
            ).finally(() => {
                isAmountChangingRef.current = false;
            });
        },
        [
            cartData,
            cartItem?.time,
            debouncedReload,
            itemKey.category,
            itemKey.dateISO,
            itemKey.id,
            itemKey.time,
        ]
    );

    return { cartItem, cartAmount, setCartAmount, handleCart };
};

export default useCartAmountSync;
